<template>
  <div class="badge" :class="state.badgeSize">
    <template v-if="icon">
      <span class="badge-inner">
        <Icon :name="icon" class="badge-icon" :class="[state.badgeSize]" role="presentation" />
        <span v-if="title" class="badge-title">{{ title }}</span>
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import type { TBadgeSize } from '~~/types/local_types';

const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  size: {
    type: String as () => TBadgeSize,
    default: 'small',
  },
  icon: {
    type: String,
    default: null,
  },
});

const state = reactive({
  badgeSize: `is--${props.size}`,
});
</script>

<style lang="postcss" scoped>
.badge {
  --badge-border-radius: var(--unit-4);
  --badge-size: var(--unit-16);
  --badge-icon-size: var(--unit-10);
  --inner-gap: var(--unit-1);
  --title-padding: var(--unit-2);

  position: relative;
  overflow: hidden;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  border-radius: var(--badge-border-radius);
  background: hsl(var(--color-primary-950));
  height: var(--badge-size);
  min-width: var(--badge-size);

  @media (--lg-viewport) {
    --inner-gap: var(--unit-2);
    --title-padding: var(--unit-4);
  }

  /* SIZE AND TYPE */
  &.is--small {
    --badge-size: var(--unit-16);

    @media (--lg-viewport) {
      --badge-size: var(--unit-32);
      --badge-icon-size: var(--unit-10);
    }

    .badge-title {
      @add-mixin label-xs-semi-bold;
    }
  }

  &.is--medium {
    --badge-size: var(--unit-32);
    --badge-icon-size: var(--unit-10);
    --title-padding: var(--unit-4);

    @media (--lg-viewport) {
      --badge-size: var(--unit-24);
      --badge-icon-size: var(--unit-14);
    }

    .badge-title {
      @add-mixin label-md-semi-bold;
    }
  }

  /*
  &.is--large {
    --badge-size: var(--unit-32);
    --badge-icon-size: var(--unit-10);

    @media (--lg-viewport) {
      --badge-size: var(--unit-40);
      --badge-icon-size: var(--unit-16);
    }
  }
   */
}

.badge-inner {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: var(--inner-gap);
  padding: calc((var(--badge-size) - var(--badge-icon-size)) / 2);
}

.badge-icon {
  display: flex;
  position: relative;
  color: hsl(var(--color-neutral-white));
  font-size: var(--badge-icon-size);
}

.badge-title {
  color: hsl(var(--color-neutral-white));
  padding: 0 var(--title-padding) 0 calc(var(--title-padding) / 2);

  /* makes it center slightly better */
  transform: translateY(var(--unit-1));
}
</style>
